import React, { useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
  Spinner,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import { HSeparator } from "components/separator/Separator";
import DefaultAuth from "layouts/auth/Default";
import illustration from "assets/img/auth/auth.png";
import { FcGoogle } from "react-icons/fc";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { useTranslation } from 'react-i18next';
import constants from '../../../providers/Constants';
import useAuthentication from "../../../providers/Authentication";
import { BlockMapDevelopersLogo, BlockMapWhiteDevelopersLogo } from "components/icons/Icons";

function SignIn() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const email = params.get('email') || '';
  const { t, i18n } = useTranslation();
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = "gray.400";
  const textColorDetails = useColorModeValue("navy.700", "secondaryGray.600");
  const textColorBrand = useColorModeValue("brand.500", "white");
  const brandStars = useColorModeValue("brand.500", "brand.400");
  const googleBg = useColorModeValue("secondaryGray.300", "whiteAlpha.200");
  const googleText = useColorModeValue("navy.700", "white");
  const googleHover = useColorModeValue(
    { bg: "gray.200" },
    { bg: "whiteAlpha.300" }
  );
  const googleActive = useColorModeValue(
    { bg: "secondaryGray.300" },
    { bg: "whiteAlpha.200" }
  );
  const shadow = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
    '14px 17px 40px 4px rgba(112, 144, 176, 0.06)',
  );
  const menuBg = useColorModeValue('white', 'navy.800');
  const [show, setShow] = React.useState(false);
  const [postData, setPostData] = React.useState({});
  const [error, setError] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const authentication = useAuthentication();
  const handleClick = () => setShow(!show);

  //   Chakra color mode
  let logoColor = useColorModeValue("navy.700", "white");

  useEffect(() => {
    const fetchData = async () => {
      try {
        await constants.clearData();
        constants.switchEnvironment('PRODUCTION', false);
      } catch (e) { }
      setPostData((prevData) => ({
        ...prevData,
        email: email,
      }));
    }
    fetchData();
  }, []);

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: "100%", md: "max-content" }}
        w='100%'
        mx={{ base: "auto", lg: "0px" }}
        me='auto'
        h='100%'
        alignItems='start'
        justifyContent='center'
        mb={{ base: "30px", md: "60px" }}
        px={{ base: "25px", md: "0px" }}
        mt={{ base: "10px", md: "5vh" }}
        flexDirection='column'>
      {/* Language Switcher - top right */}
      <Flex position='fixed' top='16px' right='24px' zIndex={1500} gap='12px'>
        {/* Language Switcher */}
        <Menu>
          <MenuButton as={Button} variant="ghost" color={textColorSecondary} px="8px">
            <Text fontSize="sm" fontWeight="600" color={textColor} textTransform="uppercase">
              {i18n.language === 'fr' ? 'FR' : 'EN'}
            </Text>
          </MenuButton>
          <MenuList boxShadow={shadow} p="10px" borderRadius="12px" bg={menuBg} border="none">
            <MenuItem onClick={() => { i18n.changeLanguage('en'); if (typeof window !== 'undefined') window.localStorage.setItem('app.lang', 'en'); }}>
              {t('English')} (EN)
            </MenuItem>
            <MenuItem onClick={() => { i18n.changeLanguage('fr'); if (typeof window !== 'undefined') window.localStorage.setItem('app.lang', 'fr'); }}>
              {t('French')} (FR)
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>
      {
        logoColor === "white" ? (
          <BlockMapWhiteDevelopersLogo h='150px' w='250px' my='0px' mx='0px' />
        ) : (
          <BlockMapDevelopersLogo h='150px' w='250px' my='0px' mx='0px' />
        )
      }
        <Box me='auto'>
          <Heading color={textColor} fontSize='36px' mb='10px'>
            {t('Sign In')}
          </Heading>
          <Text
            mb='36px'
            ms='4px'
            color={textColorSecondary}
            fontWeight='400'
            fontSize='md'>
            {t('Enter your email and password to sign in!')}
          </Text>
        </Box>
        <Flex
          zIndex='2'
          direction='column'
          w={{ base: "100%", md: "420px" }}
          maxW='100%'
          background='transparent'
          borderRadius='15px'
          mx={{ base: "auto", lg: "unset" }}
          me='auto'
          mb={{ base: "20px", md: "auto" }}>
          <FormControl>
            <FormLabel
              display='flex'
              ms='4px'
              fontSize='sm'
              fontWeight='500'
              color={textColor}
              mb='8px'>
              {t('Email')}<Text color={brandStars}>*</Text>
            </FormLabel>
            <Input
              isRequired={true}
              variant='auth'
              fontSize='sm'
              ms={{ base: "0px", md: "0px" }}
              type='email'
              placeholder='mail@blockmap.dev'
              mb='24px'
              value={postData?.email}
              fontWeight='500'
              size='lg'
              onChange={(e) => setPostData({ ...postData, email: e.target.value })}
            />
            <FormLabel
              ms='4px'
              fontSize='sm'
              fontWeight='500'
              color={textColor}
              display='flex'>
              {t('Password')}<Text color={brandStars}>*</Text>
            </FormLabel>
            <InputGroup size='md'>
              <Input
                isRequired={true}
                fontSize='sm'
                placeholder={t('Min. 12 characters')}
                mb='24px'
                size='lg'
                type={show ? "text" : "password"}
                variant='auth'
                onChange={(e) => setPostData({ ...postData, password: e.target.value })}
              />
              <InputRightElement display='flex' alignItems='center' mt='4px'>
                <Icon
                  color={textColorSecondary}
                  _hover={{ cursor: "pointer" }}
                  as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                  onClick={handleClick}
                />
              </InputRightElement>
            </InputGroup>
            <Flex justifyContent='space-between' align='center' mb='24px'>
              <FormControl display='flex' alignItems='center'>
                <Checkbox
                  id='remember-login'
                  colorScheme='brandScheme'
                  me='10px'
                />
                <FormLabel
                  htmlFor='remember-login'
                  mb='0'
                  fontWeight='normal'
                  color={textColor}
                  fontSize='sm'>
                  {t('Keep me logged in')}
                </FormLabel>
              </FormControl>
            </Flex>
            <Box>
              <Button
                fontSize='sm'
                variant='brand'
                fontWeight='500'
                w='100%'
                h='50'
                mb='24px'
                onClick={loginUser}
                isLoading={loading}
                spinner={<Spinner size="sm" />}
              >
                {t('Sign In')}
              </Button>
            </Box>
            {
              error.length > 0 && (
                error.map((err, index) => (
                  <Text
                    key={index}
                    mb='10px'
                    ms='4px'
                    color={'red'}
                    fontWeight='400'
                    fontSize='md'>
                    * {err.msg}
                  </Text>
                ))
              )
            }
          </FormControl>
          <Flex align='center' mb='25px'>
            <HSeparator />
            <Text color='gray.400' mx='14px'>
              {t('or')}
            </Text>
            <HSeparator />
          </Flex>
          <Flex
            flexDirection='column'
            justifyContent='center'
            alignItems='start'
            maxW='100%'
            mt='0px'>
            <Text color={textColorDetails} fontWeight='400' fontSize='14px'>
              {t('Not registered yet?')}
              <NavLink to='/auth/sign-up'>
                <Text
                  color={textColorBrand}
                  as='span'
                  ms='5px'
                  fontWeight='500'>
                  {t('Create an Account')}
                </Text>
              </NavLink>
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </DefaultAuth>
  );

  async function loginUser() {
    setLoading(true);
    authentication.login(postData, (error, data) => {
      setLoading(false);
      if (error) {
        setError(error.errors);
        return constants.showToast('error', 'FAILED', error?.message || 'Snap! Something went wrong, Try again');
      }
      constants.storeData('API_KEY', data?.developer?.api_key);
      constants.storeData('EMAIL', data?.user?.email);
      constants.storeData('AUTH_TOKEN', data?.user?.basic_token);
      constants.showToast('success', 'SUCCESS', 'Login successful');
      navigate('/admin');
    });
  }
}

export default SignIn;
